import { Box, Button, CircularProgress, IconButton, Typography } from "@mui/material";
import {
  InsertDriveFile as FileIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  ErrorOutline as ErrorIcon,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { color as colorTokens, fontSizes, iconSize, letterSpacings } from "../../../design-system/tokens";

const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

const formatUploadedAt = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const FileAttachmentList = ({
  attachments,
  onRemove,
  onDownload,
  onRetry,
  canDelete = true,
}) => {
  if (attachments.length === 0) {
    return null;
  }

  return (
    <Box
      data-testid="file-attachment-list"
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
        width: "100%",
      }}
    >
      {attachments.map((attachment) => {
        // LWI-869 — visually distinguish failed uploads so the supplier can
        // see exactly which file did not persist and retry it.
        const isFailed = !!attachment.uploadFailed;
        return (
        <Box
          key={attachment.id}
          data-testid={`attachment-${attachment.id}`}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            p: 1.5,
            border: 1,
            borderColor: isFailed ? "error.main" : "divider",
            bgcolor: isFailed ? (theme) => alpha(theme.palette.error.main, theme.palette.action.hoverOpacity) : "transparent",
            borderRadius: 1,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: isFailed
                ? "error.main"
                : attachment.isUploading
                ? (theme) => colorTokens[theme.palette.mode].callout.infoBorder
                : "info.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {attachment.isUploading ? (
              <CircularProgress size={20} sx={{ color: "common.white" }} />
            ) : isFailed ? (
              <ErrorIcon sx={{ color: "common.white", fontSize: iconSize.md }} />
            ) : (
              <FileIcon sx={{ color: "common.white", fontSize: iconSize.md }} />
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: fontSizes.base,
                color: "text.primary",
                fontWeight: 400,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 200,
              }}
            >
              {attachment.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: fontSizes.sm,
                color: isFailed ? "error.main" : "text.secondary",
                fontWeight: isFailed ? 600 : 400,
                letterSpacing: letterSpacings.button,
              }}
            >
              {isFailed ? (
                <>Upload failed — please retry or remove</>
              ) : (
                <>
                  {formatFileSize(attachment.size)}
                  {attachment.uploadedAt && ` · ${formatUploadedAt(attachment.uploadedAt)}`}
                </>
              )}
            </Typography>
          </Box>

          {isFailed && onRetry && (
            <Button
              data-testid={`retry-attachment-${attachment.id}`}
              size="small"
              variant="outlined"
              color="error"
              onClick={() => onRetry(attachment.id)}
              sx={{
                ml: 0.5,
                px: 1.5,
                py: 0.25,
                fontSize: fontSizes.sm,
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              Retry
            </Button>
          )}
          {onDownload && !attachment.isUploading && !isFailed && (
            <IconButton
              data-testid={`download-attachment-${attachment.id}`}
              size="small"
              onClick={() => onDownload(attachment.id, attachment.name)}
              sx={{
                ml: 0.5,
                color: "grey.600",
                "&:hover": { color: "primary.main" },
              }}
            >
              <DownloadIcon sx={{ fontSize: iconSize.md }} />
            </IconButton>
          )}
          {(canDelete || attachment.isUploading) && (
            <IconButton
              data-testid={`delete-attachment-${attachment.id}`}
              size="small"
              onClick={() => onRemove(attachment.id)}
              disabled={attachment.isUploading}
              sx={{
                ml: 0.5,
                color: "grey.600",
                "&:hover": {
                  color: "error.main",
                },
              }}
            >
              <DeleteIcon sx={{ fontSize: iconSize.md }} />
            </IconButton>
          )}
        </Box>
        );
      })}
    </Box>
  );
};
